import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { format } from 'date-fns';
import { DB } from '~/services/firebase';
import Dashboard from '~/layouts/Dashboard';
import { useAuth } from '~/hooks/auth';
import getCurrencyFormatted from '~/utils/getCurrencyFormatted';
import IOrder from '~/interfaces/IOrder';
import { PAYMENT_METHODS } from '~/utils/getEnums';
import ICartProduct from '~/interfaces/ICartProduct';
import { useProductsCompany } from '~/hooks/productsCompany';
import IComplement from '~/interfaces/IComplement';
import { CashFlow } from '~/components/CashFlow';
import { DeliveryFeeDetails } from '~/components/DeliveryFeeDetails';
import { useCashFlow } from '~/hooks/cashFlow';

type ResumeProduct = {
  uid: string;
  name: string;
  quantity: number;
  isPizzaComplement: boolean;
};

const paymentMethods = [
  'DINHEIRO',
  'DÉBITO',
  'CRÉDITO',
  'PIX',
  'FIADO',
  'IFOOD',
  'NÃO DEFINIDO',
];

const Resume: React.FC = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState<IOrder[]>([]);

  const [initialDate, setInitialDate] = useState(
    format(new Date(), 'yyyy-MM-dd'),
  );
  const [initialHour, setInitialHour] = useState('00:00');
  const [finalDate, setFinalDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [finalHour, setFinalHour] = useState('23:59');
  const [totalDelivery, setTotalDelivery] = useState(0);
  // const [loading, setLoading] = useState(false);
  // const [errors, setErrors] = useState({} as any);

  const { loadComplements, complements: groups } = useProductsCompany();
  const { launchs } = useCashFlow();

  useEffect(() => {
    if (!groups || groups.length === 0) {
      loadComplements();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadComplements]);

  function countComplements(complements: any[], group: IComplement) {
    return complements.filter(comp => comp.group === group.uid).length;
  }

  const products = useMemo(() => {
    let list: ICartProduct[] = [];

    orders.forEach(orderItem => {
      list = [...list, ...orderItem.products];
    });

    const grouped: ResumeProduct[] = [];

    list.forEach(item => {
      const itemHasComplements =
        item.complements && item.complements.length > 0;

      if (itemHasComplements) {
        item.complements.forEach(complement => {
          const group = groups?.find(
            findGroup => findGroup.uid === complement.group,
          );

          const findIndex = grouped.findIndex(
            findItem => findItem.uid === complement.item.uid,
          );

          const count = group ? countComplements(item.complements, group) : 0;

          if (findIndex === -1) {
            const parseUid = complement.item.uid;
            const parseName = `${item.name} - ${complement.item.name}`;
            const quantity = group ? item.quantity / count : item.quantity;

            // console.log(parseName, quantity);

            grouped.push({
              uid: parseUid,
              name: parseName,
              quantity,
              isPizzaComplement: group?.pizzaType === true,
            });
          } else {
            const quantity = group ? item.quantity / count : item.quantity;
            grouped[findIndex].quantity += quantity;

            // console.log(grouped[findIndex].name, quantity);
          }
        });
      } else {
        const findIndex =
          item.complements && item.complements.length > 0
            ? grouped.findIndex(
                findItem => findItem.uid === item.complements[0].item.uid,
              )
            : grouped.findIndex(findItem => findItem.uid === item.uid);

        if (findIndex === -1) {
          const { uid, name, quantity } = item;

          const parseUid =
            item.complements && item.complements.length > 0
              ? item.complements[0].item.uid
              : uid;

          const parseName =
            item.complements && item.complements.length > 0
              ? `${name} - ${item.complements[0].item.name}`
              : name;

          grouped.push({
            uid: parseUid,
            name: parseName,
            quantity,
            isPizzaComplement: false,
          });
        } else {
          grouped[findIndex].quantity += item.quantity;
        }
      }
    });

    grouped.sort((a, b) => a.quantity - b.quantity);

    return grouped.reverse();
  }, [orders, groups]);

  const countPizzas = useMemo(() => {
    return products.reduce(
      (count, item) => count + (item.isPizzaComplement ? item.quantity : 0),
      0,
    );
  }, [products]);

  const couponStats = useMemo(() => {
    const stats: { [key: string]: { count: number; total: number } } = {};

    orders.forEach(order => {
      if (order.coupon && order.coupon.uid && order.status !== '4') {
        const code = order.coupon.code;
        if (!stats[code]) {
          stats[code] = { count: 0, total: 0 };
        }
        stats[code].count += 1;
        stats[code].total += order.total;
      }
    });

    return stats;
  }, [orders]);

  const resume = useMemo(() => {
    const data = {} as any;
    let total = 0;

    paymentMethods.forEach((item: any) => {
      data[item] = 0;
    });

    orders.forEach(item => {
      if (item.orderPayments && item.orderPayments.length > 0) {
        item.orderPayments.forEach(payment => {
          const _paymentMethod =
            PAYMENT_METHODS[payment.payment_method_id || 99];
          data[_paymentMethod] += payment.value;
        });
      } else {
        const paymentMethod = PAYMENT_METHODS[item.paymentMethod || 99];
        if (item.status !== '4') {
          data[paymentMethod] += item.total;
        }
      }

      // data[paymentMethod] += item.total;
      // total += item.total;

      if (item.status !== '4') {
        total += item.total;
      }
    });

    return { data, total };
  }, [orders]);

  const netRevenue = useMemo(() => {
    let entradas = 0;
    let saidas = 0;

    launchs.forEach(item => {
      const value = Number(parseFloat(String(item.value))) || 0;

      if (item.type === '1') entradas += value;
      if (item.type === '0') saidas += value;
    });

    return (Number(resume.total) || 0) + entradas - saidas;
  }, [launchs, resume.total]);

  const loadOrders = useCallback(async () => {
    const [iHour, iMinute] = initialHour.split(':');
    const [eHour, eMinute] = finalHour.split(':');

    const initDate = new Date(initialDate);
    initDate.setHours(parseInt(iHour));
    initDate.setMinutes(parseInt(iMinute));
    initDate.setSeconds(0);
    initDate.setDate(initDate.getDate() + 1);

    const finishDate = new Date(finalDate);
    finishDate.setHours(parseInt(eHour));
    finishDate.setMinutes(parseInt(eMinute));
    finishDate.setSeconds(59);
    finishDate.setDate(finishDate.getDate() + 1);

    console.log(initDate, finishDate);

    const q = query(
      collection(DB, 'orders'),
      where('company_user_id', '==', user.uid),
      where('created_at', '>=', initDate),
      where('created_at', '<=', finishDate),
    );

    const querySnapshot = await getDocs(q);

    const items: IOrder[] = [];
    let sumDelivery = 0;

    querySnapshot.docs.forEach((item: any) => {
      const data = item.data() as IOrder;
      const created_at = new Date(data.created_at.seconds * 1000);

      sumDelivery += data.shippingCost;

      const parseItem: IOrder = {
        ...data,
        uid: item.id,
        shippingCost_formatted: getCurrencyFormatted(data.shippingCost),
        subtotal_formatted: getCurrencyFormatted(data.subtotal),
        total_formatted: getCurrencyFormatted(data.total),
        created_at_formatted: format(created_at, 'dd/MM/yyyy HH:mm'),
      };

      items.push(parseItem);
    });

    setTotalDelivery(sumDelivery);
    setOrders(items);
  }, [user, initialDate, initialHour, finalDate, finalHour]);

  useEffect(() => {
    if (user && user.uid) loadOrders();
  }, [loadOrders, user]);

  return (
    <Dashboard containerMessage="col-xl-8 offset-xl-2 mb-4">
      <div className="row">
        <div className="col-xl-8 offset-xl-2 mb-4">
          <div className="nk-block nk-block-lg">
            <div className="nk-block-head">
              <div className="nk-block-head-content">
                <h4 className="title nk-block-title">
                  Resumo Diário de Vendas
                </h4>
                <div className="nk-block-des">
                  <p>Consulte as vendas de um dia específico.</p>
                </div>
              </div>
            </div>
            <div className="card card-bordered">
              <div className="card-inner">
                <form onSubmit={() => {}}>
                  <div className="row">
                    <div className="col-sm-3">
                      <div className="form-group mb-3">
                        <label className="form-label">Data Inicial</label>
                        <div className="form-control-wrap">
                          <input
                            id="date"
                            type="date"
                            step=".01"
                            className="form-control"
                            defaultValue={initialDate}
                            onChange={event => {
                              setInitialDate(event.target.value);
                            }}
                          />
                          {/* {errors.from && (
                            <span className="invalid">{errors.from}</span>
                          )} */}
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-3">
                      <div className="form-group mb-3">
                        <label className="form-label">Hora Inicial</label>
                        <div className="form-control-wrap">
                          <input
                            id="date"
                            type="time"
                            className="form-control"
                            defaultValue={initialHour}
                            onChange={event => {
                              setInitialHour(event.target.value);
                            }}
                          />
                          {/* {errors.from && (
                            <span className="invalid">{errors.from}</span>
                          )} */}
                        </div>
                      </div>
                    </div>

                    <div className="col-sm-3">
                      <div className="form-group mb-3">
                        <label className="form-label">Data Final</label>
                        <div className="form-control-wrap">
                          <input
                            id="date"
                            type="date"
                            step=".01"
                            className="form-control"
                            defaultValue={finalDate}
                            onChange={event => {
                              setFinalDate(event.target.value);
                            }}
                          />
                          {/* {errors.from && (
                            <span className="invalid">{errors.from}</span>
                          )} */}
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-3">
                      <div className="form-group mb-3">
                        <label className="form-label">Hora Final</label>
                        <div className="form-control-wrap">
                          <input
                            id="date"
                            type="time"
                            className="form-control"
                            defaultValue={finalHour}
                            onChange={event => {
                              setFinalHour(event.target.value);
                            }}
                          />
                          {/* {errors.from && (
                            <span className="invalid">{errors.from}</span>
                          )} */}
                        </div>
                      </div>
                    </div>

                    {/* <div
                      className="col-sm-6 d-flex"
                      style={{ alignItems: 'flex-end' }}
                    >
                      <div className="form-group mb-3">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading && (
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            />
                          )}
                          {!loading && 'Consultar'}
                        </button>
                      </div>
                    </div> */}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-8 offset-xl-2">
          <div className="row g-3 mb-5">
            <div className="col-md-4">
              <div className="card card-bordered h-100">
                <div className="card-inner">
                  <div className="d-flex align-items-center">
                    <em className="icon icon-circle bg-primary-dim ni ni-sign-dollar mr-3" />
                    <div>
                      <span className="d-block fs-11px fw-normal text-soft mb-1">
                        Faturamento Bruto
                      </span>
                      <div className="analytic-data">
                        <div className="amount fw-bold mb-0">
                          {getCurrencyFormatted(resume.total)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card card-bordered h-100">
                <div className="card-inner">
                  <div className="d-flex align-items-center">
                    <em className="icon icon-circle bg-success-dim ni ni-wallet-in mr-3" />
                    <div>
                      <span className="d-block fs-11px fw-normal text-soft mb-1">
                        Faturamento Líquido
                      </span>
                      <div className="analytic-data">
                        <div className="amount fw-bold mb-0">
                          {getCurrencyFormatted(netRevenue)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card card-bordered h-100">
                <div className="card-inner">
                  <div className="d-flex align-items-center">
                    <em className="icon icon-circle bg-info-dim ni ni-truck mr-3" />
                    <div>
                      <span className="d-block fs-11px fw-normal text-soft mb-1">
                        Taxa de Entrega
                      </span>
                      <div className="analytic-data">
                        <div className="amount fw-bold mb-0">
                          {getCurrencyFormatted(totalDelivery)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="nk-block mt-2">
            <div className="card card-bordered">
              <div className="card-inner-group">
                <div className="card-inner p-0">
                  <div className="nk-tb-list">
                    <div className="nk-tb-item nk-tb-head">
                      <div className="nk-tb-col">
                        <span>Forma de Pagamento</span>
                      </div>
                      <div className="nk-tb-col">
                        <span>Valor</span>
                      </div>
                    </div>

                    {Object.keys(resume.data).map(field => (
                      <div className="nk-tb-item" key={field}>
                        <div className="nk-tb-col">
                          <span className="tb-product">{field}</span>
                        </div>
                        <div className="nk-tb-col">
                          <span className="tb-product">
                            {getCurrencyFormatted(resume.data[field])}
                          </span>
                        </div>
                      </div>
                    ))}

                    {/* <div className="nk-tb-item">
                      <div className="nk-tb-col">
                        <span className="tb-product">TAXA DE ENTREGA</span>
                      </div>
                      <div className="nk-tb-col">
                        <span className="tb-product">
                          {getCurrencyFormatted(totalDelivery)}
                        </span>
                      </div>
                    </div> */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {Object.keys(couponStats).length > 0 && (
          <div className="col-xl-8 offset-xl-2 mt-4">
            <div className="nk-block-head nk-block-head-sm">
              <div className="nk-block-between">
                <div className="nk-block-head-content w-100">
                  <h6 className="nk-block-title">Cupons de Desconto Utilizados</h6>
                </div>
              </div>
            </div>
            <div className="nk-block">
              <div className="card card-bordered">
                <div className="card-inner-group">
                  <div className="card-inner p-0">
                    <div className="nk-tb-list">
                      <div className="nk-tb-item nk-tb-head">
                        <div className="nk-tb-col">
                          <span>Cupom</span>
                        </div>
                        <div className="nk-tb-col">
                          <span>Quantidade de Pedidos</span>
                        </div>
                        <div className="nk-tb-col">
                          <span>Total em Vendas</span>
                        </div>
                      </div>

                      {Object.keys(couponStats).map(code => (
                        <div className="nk-tb-item" key={code}>
                          <div className="nk-tb-col">
                            <span className="tb-product">{code}</span>
                          </div>
                          <div className="nk-tb-col">
                            <span className="tb-product">
                              {couponStats[code].count} pedidos
                            </span>
                          </div>
                          <div className="nk-tb-col">
                            <span className="tb-product">
                              {getCurrencyFormatted(couponStats[code].total)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <CashFlow
          initDate={initialDate}
          endDate={finalDate}
          initHour={initialHour}
          endHour={finalHour}
          moneyValue={resume.data.DINHEIRO}
          orders={orders}
        />

        <DeliveryFeeDetails orders={orders} />

        <div className="col-xl-8 offset-xl-2 mt-4">
          <div className="nk-block-head nk-block-head-sm">
            <div className="nk-block-between">
              <div className="nk-block-head-content w-100 d-flex justify-content-between">
                <h6 className="nk-block-title">{`Produtos vendidos (${orders.length} pedidos)`}</h6>
                {countPizzas > 0 && (
                  <h6 className="nk-block-title">{`Quantidade Pizzas (${countPizzas})`}</h6>
                )}
              </div>
            </div>
          </div>
          <div className="nk-block">
            <div className="card card-bordered">
              <div className="card-inner-group">
                <div className="card-inner p-0">
                  <div className="nk-tb-list">
                    <div className="nk-tb-item nk-tb-head">
                      <div className="nk-tb-col">
                        <span>Produto</span>
                      </div>
                      <div className="nk-tb-col">
                        <span>Quantidade</span>
                      </div>
                    </div>

                    {products.map(item => (
                      <div className="nk-tb-item" key={item.uid}>
                        <div className="nk-tb-col">
                          <span className="tb-product">{item.name}</span>
                        </div>
                        <div className="nk-tb-col">
                          <span className="tb-product">{item.quantity}</span>
                        </div>
                      </div>
                    ))}

                    {/* <div className="nk-tb-item">
                      <div className="nk-tb-col">
                        <span className="tb-product">TAXA DE ENTREGA</span>
                      </div>
                      <div className="nk-tb-col">
                        <span className="tb-product">
                          {getCurrencyFormatted(totalDelivery)}
                        </span>
                      </div>
                    </div> */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dashboard>
  );
};

export default Resume;
